import type {
  GetAllResolvedEdiblesResponse,
  GetMenuAppRestaurantDataResponse,
  GetMenuResponseV2,
  LoadableResponse,
  Restaurant,
} from "api-types";

import { useEffect, useMemo } from "react";

import {
  RestaurantBrandingAndTheme,
  createRestaurantBrandingAndTheme,
} from "igfd-ui/src/app-index";

import {
  priceFormatterForRestaurant,
  useAllResolvedEdibles,
  useMenuByMenuUrlQuery,
  useThrowAsyncError,
} from "igfd-commons/src/app-index";

import { setStaffContext } from "../monitoring/mixpanel";
import { setRestaurantCtxForMonitoring } from "../monitoring/rum";
import { client } from "../queries/axios-client";
import {
  createAnalyticsCtx,
  createRestaurantObjectForLocation,
} from "./restaurantObjectForLocation";

type UseMenuAppResult = {
  menuResponse: LoadableResponse<GetMenuResponseV2>;
  linkedItems: GetAllResolvedEdiblesResponse | undefined;
  priceFormatter: (priceInLowestDenomination: number) => string;
  restaurant: Restaurant;
  brandName: string;
  restaurantBrandingAndTheme: RestaurantBrandingAndTheme;
};

/**
 * The core hook responsible for data fetching and global state management for the restaurant menu.
 * This is loaded once the restaurant is known, and the urlSlug has been mapped to a restaurantUuid.
 */
export const useMenuApp = ({
  restaurantData,
  urlSlug,
  menuId,
}: {
  restaurantData: GetMenuAppRestaurantDataResponse;
  urlSlug: string;
  menuId?: string;
}): UseMenuAppResult => {
  const restaurant = createRestaurantObjectForLocation(restaurantData);
  const brandName = restaurantData.branding?.brandName || restaurant.name;
  const restaurantUuid = restaurant.uuid;

  const {
    data: menu,
    isLoading: isMenuLoading,
    error: menuError,
    isRefetchError: isMenuRefetchError,
  } = useMenuByMenuUrlQuery(client, urlSlug, menuId);

  const {
    data: linkedItems,
    isLoading: isLinkedLoading,
    error: linkedError,
  } = useAllResolvedEdibles(client, restaurantUuid);

  const throwAsyncError = useThrowAsyncError();

  useEffect(() => {
    const error = menuError || linkedError;
    if (error && !isMenuRefetchError) {
      // We don't want to show the error page if we are just refetching
      throwAsyncError(error as Error);
    }
  }, [menuError, linkedError, isMenuRefetchError]);

  // set restaurant context for monitoring
  useEffect(() => {
    const partialAnalyticsCtx = createAnalyticsCtx(restaurantData);
    setRestaurantCtxForMonitoring({
      ...partialAnalyticsCtx,
      source: null,
      menuId,
    });
    setStaffContext({
      workspace_id: partialAnalyticsCtx.uuid,
      restaurant_name: partialAnalyticsCtx.name,
      location_id: partialAnalyticsCtx.locationId ?? null,
      menu_url: urlSlug,
    });
  }, [menuId, urlSlug]);

  // Restaurant Theme (based on branding config provided by the restaurant)
  const restaurantBrandingAndTheme =
    createRestaurantBrandingAndTheme(restaurantData);

  const priceFormatter = useMemo(
    () => priceFormatterForRestaurant(restaurant),
    [restaurant]
  );

  return {
    menuResponse: {
      data: menu,
      isLoading: isMenuLoading || isLinkedLoading,
    },
    linkedItems,
    priceFormatter,
    restaurant,
    brandName,
    restaurantBrandingAndTheme,
  };
};
